import type { JSX } from "react";
import { useMemo } from "react";

import styled from "styled-components";

import { colorsPalettes, rgba } from "@similarweb/styles";
import { MiniFlexTable } from "@similarweb/ui-components";

import { DefaultCell, GroupTrafficShare, TrafficShare } from "app/components/React/Table/cells";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import { WidgetLoader } from "app/components/widget-loader/WidgetLoader";
import { i18nFilter } from "app/filters/i18nFilter";
import { EmptyState } from "app/pages/website-analysis/traffic-sources/display-ads/common/EmptyState";
import { availableDataTypes } from "app/pages/website-analysis/traffic-sources/display-ads/common/DisplayAdsTypes";
import type { PieChartDataItemType } from "app/pages/website-analysis/traffic-sources/display-ads/common/PieChart";
import { PieChart } from "app/pages/website-analysis/traffic-sources/display-ads/common/PieChart";
import { buildAdNetworksRequestParams } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/helpers/buildAdNetworksRequestParams";
import {
    createAdNetworksRowUrlBuilder,
    mapAdNetworksTableRows,
    normalizeAdNetworksTableResponse,
} from "app/pages/sales-intelligence/sub-modules/account-overview/shared/helpers/mapAdNetworksTableRows";
import { transformAdNetworksPieChartData } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/helpers/transformAdNetworksPieChartData";
import useAdNetworksPieChartQuery from "app/pages/sales-intelligence/sub-modules/account-overview/shared/query/queries/useAdNetworksPieChartQuery";
import useAdNetworksTableQuery from "app/pages/sales-intelligence/sub-modules/account-overview/shared/query/queries/useAdNetworksTableQuery";
import { allTrackers } from "app/services/track/track";
import { CenteredFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const ContentWrapper = styled(CenteredFlexRow)`
    height: 284px;
`;

const PieChartWrapper = styled.div`
    height: 232px;
    > div {
        height: inherit;
    }
`;

const CenteredCell = styled.div`
    height: 100%;
    align-items: center;
    display: flex;
`;

const StyledTable = styled(MiniFlexTable)`
    &.MiniFlexTable {
        padding-bottom: 0;
        min-height: 232px;
        flex: 1;
    }

    .MiniFlexTable-cell {
        height: 40px;
        vertical-align: middle;
        font-size: 14px;

        &:not(:first-child) {
            border-top: 1px solid ${colorsPalettes.carbon[50]};
        }
    }

    .MiniFlexTable-column {
        &:first-of-type {
            .MiniFlexTable-headerCell,
            .MiniFlexTable-cell {
                padding-left: 24px;
            }
        }

        &:last-of-type {
            .MiniFlexTable-headerCell,
            .MiniFlexTable-cell {
                padding-right: 24px;
            }
        }

        &:nth-of-type(2) {
            .MiniFlexTable-cell {
                > div {
                    height: 100%;
                    align-items: center;
                    display: flex;
                }
            }
        }
    }

    .MiniFlexTable-headerCell {
        margin-bottom: 2px;
        font-weight: 500;
        font-size: 12px;
        color: ${rgba(colorsPalettes.carbon[500], 0.6)};
    }
`;

export type WWOTopAdNetworksSIParams = Record<string, unknown> & {
    country: number | string;
    key: string;
    duration: string;
    isWWW?: string;
};

export interface IWWOTopAdNetworksSIComponentsProps {
    getCta: (ctaClicked: () => void) => JSX.Element | null;
    params: WWOTopAdNetworksSIParams;
    isCompare: boolean;
}

export const WWOTopAdNetworksPieChartSI = ({ getCta, params }: IWWOTopAdNetworksSIComponentsProps) => {
    const i18n = i18nFilter();
    const requestParams = useMemo(() => buildAdNetworksRequestParams(params), [params]);
    const { data: response, isLoading } = useAdNetworksPieChartQuery(requestParams);
    const data = useMemo((): PieChartDataItemType[] => {
        const domainData = response?.data?.[params.key];

        return domainData ? transformAdNetworksPieChartData(domainData) : [];
    }, [params.key, response?.data]);

    const ctaClicked = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "Display Ad Network/Pie Chart/See More Ad Networks");
    };

    const getComponent = () => {
        const tooltipHeaders = [i18n("wa.wwo.topadnetworks.domain"), i18n("wa.ao.graph.trafficshare")];
        const pieChartConfig = {
            chartType: availableDataTypes[1],
            tooltipHeaders,
            tooltipContainerWidth: 280,
            isEmptyStateSmall: true,
        };

        return data.length > 0 ? (
            <>
                <PieChartWrapper>
                    <PieChart
                        pieChartData={data}
                        pieChartConfig={pieChartConfig}
                    />
                </PieChartWrapper>
                {getCta(ctaClicked)}
            </>
        ) : (
            <ContentWrapper>
                <EmptyState
                    titleKey="wa.wwo.no.data.title"
                    subTitleKey="wa.wwo.no.data.sub.title"
                />
            </ContentWrapper>
        );
    };

    return isLoading ? (
        <ContentWrapper>
            <WidgetLoader />
        </ContentWrapper>
    ) : (
        getComponent()
    );
};

export const WWOTopAdNetworksTableSI = ({ getCta, params }: IWWOTopAdNetworksSIComponentsProps) => {
    const i18n = i18nFilter();
    const requestParams = useMemo(() => buildAdNetworksRequestParams(params, { pageSize: 5 }), [params]);
    const { data: response, isLoading } = useAdNetworksTableQuery(requestParams);
    const getRowUrl = useMemo(
        () => createAdNetworksRowUrlBuilder(params, (state, rowParams) => proNavigator.href(state, rowParams)),
        [params],
    );
    const data = useMemo(
        () => mapAdNetworksTableRows(normalizeAdNetworksTableResponse(response), getRowUrl),
        [getRowUrl, response],
    );
    const columns = useMemo(
        () => [
            {
                field: "Domain",
                displayName: i18n("wa.wwo.topadnetworks.domain"),
                headerComponent: DefaultCellHeader,
                cellComponent: (props) => (
                    <CenteredCell>
                        <DefaultCell {...props} />
                    </CenteredCell>
                ),
            },
            {
                field: "TotalShare",
                displayName: i18n("wa.wwo.topadnetworks.traffic.share"),
                headerComponent: DefaultCellHeader,
                cellComponent: TrafficShare,
            },
            {
                field: "SiteOrigins",
                displayName: i18n("wa.wwo.topadnetworks.share.split"),
                cellComponent: (props) => (
                    <CenteredCell>
                        <GroupTrafficShare {...props} />
                    </CenteredCell>
                ),
                isResizable: false,
                sortable: false,
            },
        ],
        [i18n],
    );

    const ctaClicked = () => {
        allTrackers.dispatchTrackEvent(
            "Internal Link",
            "click",
            "Display Ad Network/Table/See more display ad networks",
        );
    };

    const getComponent = () =>
        data.length > 0 ? (
            <>
                <StyledTable
                    className="MiniFlexTable"
                    data={data}
                    columns={columns}
                />
                {getCta(ctaClicked)}
            </>
        ) : (
            <ContentWrapper>
                <EmptyState
                    titleKey="wa.wwo.no.data.title"
                    subTitleKey="wa.wwo.no.data.sub.title"
                />
            </ContentWrapper>
        );

    return isLoading ? (
        <ContentWrapper>
            <WidgetLoader />
        </ContentWrapper>
    ) : (
        getComponent()
    );
};
